/**
 * Lazy loader for Apple MapKit JS (the browser equivalent of the native
 * MapKit used by the iOS `FlightMap` Capacitor plugin). Used by
 * `FlightMapWeb` to render the desktop web home map background.
 *
 * The script is injected from Apple's CDN on first use and `mapkit.init()` is
 * called with the static JWT from `VITE_MAPKIT_TOKEN`. The in-flight promise is
 * cached so concurrent callers share a single load.
 */
const MAPKIT_SRC = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js'

let loadPromise: Promise<typeof mapkit> | null = null

export function loadMapKit(): Promise<typeof mapkit> {
  if (loadPromise) return loadPromise

  loadPromise = new Promise<typeof mapkit>((resolve, reject) => {
    const token = import.meta.env.VITE_MAPKIT_TOKEN
    if (!token) {
      reject(new Error('Missing VITE_MAPKIT_TOKEN environment variable'))
      return
    }

    // Resolve once MapKit signals it has finished configuring with the token.
    const onReady = () => resolve(window.mapkit as typeof mapkit)

    const init = () => {
      const mk = window.mapkit
      if (!mk) {
        reject(new Error('MapKit JS loaded but window.mapkit is undefined'))
        return
      }
      mk.addEventListener('configuration-change', onReady)
      mk.addEventListener('error', () =>
        reject(new Error('MapKit JS failed to initialise (check the token)')),
      )
      mk.init({
        authorizationCallback: (done) => done(token),
      })
      // `configuration-change` may have fired synchronously during init on a
      // warm cache; resolve defensively on the next tick if mapkit is usable.
      queueMicrotask(() => {
        if (mk.Map) resolve(mk)
      })
    }

    // Already present (e.g. a previous load): init directly.
    if (window.mapkit) {
      init()
      return
    }

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${MAPKIT_SRC}"]`,
    )
    if (existing) {
      existing.addEventListener('load', init)
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load MapKit JS')),
      )
      return
    }

    const script = document.createElement('script')
    script.src = MAPKIT_SRC
    script.async = true
    script.crossOrigin = 'anonymous'
    script.addEventListener('load', init)
    script.addEventListener('error', () =>
      reject(new Error('Failed to load MapKit JS')),
    )
    document.head.appendChild(script)
  }).catch((err) => {
    // Allow a later retry if the first attempt fails.
    loadPromise = null
    throw err
  })

  return loadPromise
}
